import { isIndianEmployee } from '@shared/constants/countries';

/**
 * Interface defining all evaluation terminology variants
 * Supports different terminologies based on employee location (e.g., "probation" vs "trial")
 */
export interface EvaluationTerms {
  /** The type of evaluation period ("probation" for India, "trial" for others) */
  termType: 'probation' | 'trial';
  /** Text for end of evaluation period (e.g., "End of Probation", "End of Trial") */
  endOfPeriod: string;
  /** Abbreviation for end of evaluation period (e.g., "EoP", "EoT") */
  endOfPeriodAbbr: string;
  /** Label for the evaluation period type (e.g., "Probation period", "Trial period") */
  periodLabel: string;
  /** Action text for extending the period (e.g., "Extend probation period") */
  extendPeriod: string;
  /** Text for period extension deadline (e.g., "Extend probation period to") */
  extendedTo: string;
  /** Description when period failed (e.g., "probation period is failed") */
  periodFailed: string;
  /** Accounting note explaining period resolution usage */
  accountingNote: string;
  /** Action text for marking as passed (e.g., "Set probation as passed") */
  setAsPassed: string;
  /** Action text for marking as failed (e.g., "Set probation as failed") */
  setAsFailed: string;
  /** Confirmation button text (e.g., "Confirm probation result") */
  confirmButtonText: string;
  /** Text for when period ended (e.g., "Probation period ended on") */
  periodEndedOn: string;
  /** Text for when period extended (e.g., "Probation period extended to") */
  periodExtendedTo: string;
  /** Text for when period ends (e.g., "Probation period ends on") */
  periodEndsOn: string;
  /** Text for extended period modal title (e.g., "Extended probation period") */
  extendedPeriodTitle: string;
  /** Text for period will be extended (e.g., "Probation period will be extended to") */
  periodWillBeExtendedTo: string;
  /** Text for close period action (e.g., "Close probation period") */
  closePeriod: string;
  /** Text for period passed successfully (e.g., "End of probation period is passed successfully!") */
  periodPassedSuccessfully: string;
  /** Text for wait set resolution modal (e.g., "Wait... set end of probation period resolution") */
  waitSetResolution: string;
  /** Text for period established notification (e.g., "Probation period established. Status:") */
  periodEstablished: string;
  /** Text for period could not be established (e.g., "end of the probation period could not be established") */
  periodCouldNotBeEstablished: string;
  /** Text for passing period check (e.g., "passing probation period") */
  passingPeriod: string;
  /** Modal title for passing period (e.g., "Passing probation period") */
  periodPassingTitle: string;
  /** Accounting note for passed status (e.g., "Accounting report will list {name} with passed probation status.") */
  accountingPassedNote: string;
  /** Modal title for failed period (e.g., "Probation period failed") */
  periodFailedTitle: string;
  /** Placeholder for failing reason (e.g., "Leave a note why the probation period is failed") */
  failingReasonPlaceholder: string;
  /** Placeholder for extension reason (e.g., "Leave a note why the probation period is being extended") */
  extendReasonPlaceholder: string;
  /** Field label for failing reason */
  failingReasonLabel: string;
  /** Field label for extension reason */
  extendReasonLabel: string;
  /** Note explaining that extension creates a new evaluation record */
  extensionNewRecordNote: string;
}

/**
 * Generic evaluation messages that don't vary by probation/trial terminology
 */
export const evaluationMessages = {
  requiredJustificationHelper: 'Leaving a written justification is required for the result to be saved',
};

/**
 * Gets the appropriate evaluation terminology based on employee country
 *
 * Returns "probation" terminology for India-based employees,
 * "trial" terminology for all other countries.
 *
 * @param userCountry - The employee's country (case-insensitive, optional)
 * @returns EvaluationTerms object with appropriate terminology
 *
 * @example
 * ```typescript
 * // India-based employee
 * getEvaluationTerms('India')
 * // Returns: { termType: 'probation', endOfPeriod: 'End of Probation', ... }
 *
 * // Non-India employee
 * getEvaluationTerms('USA')
 * // Returns: { termType: 'trial', endOfPeriod: 'End of Trial', ... }
 *
 * // Default case
 * getEvaluationTerms(undefined)
 * // Returns: { termType: 'trial', endOfPeriod: 'End of Trial', ... }
 * ```
 */
export const getEvaluationTerms = (userCountry?: string): EvaluationTerms => {
  const isIndia = isIndianEmployee(userCountry);

  if (isIndia) {
    return {
      termType: 'probation',
      endOfPeriod: 'End of probation',
      endOfPeriodAbbr: 'EoP',
      periodLabel: 'Probation period',
      extendPeriod: 'Extend probation period',
      extendedTo: 'Extend probation period to',
      periodFailed: 'probation period is failed',
      accountingNote: 'Finance team uses end of probation period resolutions in their reports and calculations.',
      setAsPassed: 'Set probation as passed',
      setAsFailed: 'Set probation as failed',
      confirmButtonText: 'Confirm probation result',
      periodEndedOn: 'Probation period ended on',
      periodExtendedTo: 'Probation period extended to',
      periodEndsOn: 'Probation period ends on',
      extendedPeriodTitle: 'Extended probation period',
      periodWillBeExtendedTo: 'Probation period will be extended to',
      closePeriod: 'Close probation period',
      periodPassedSuccessfully: 'End of probation period is passed successfully!',
      waitSetResolution: 'Wait... set end of probation period resolution',
      periodEstablished: 'Probation period established. Status:',
      periodCouldNotBeEstablished: 'end of the probation period could not be established',
      passingPeriod: 'passing probation period',
      periodPassingTitle: 'Passing probation period',
      accountingPassedNote:
        'Finance report will list {name} with passed probation status; however, the feedback still has to be gathered, and the evaluation has to be closed for it to be completed and the evaluatee notified via email.',
      periodFailedTitle: 'Probation period failed',
      failingReasonPlaceholder: 'Leave a note why the probation period is failed',
      extendReasonPlaceholder: 'Leave a note why the probation period is being extended',
      failingReasonLabel: 'Leave a note why the probation period is failed',
      extendReasonLabel: 'Leave a note why the probation period is being extended',
      extensionNewRecordNote:
        'Selecting "Extend probation period" will generate a new evaluation record, where a preliminary resolution and feedback will be required again, at the new date.',
    };
  }

  return {
    termType: 'trial',
    endOfPeriod: 'End of trial',
    endOfPeriodAbbr: 'EoT',
    periodLabel: 'Trial period',
    extendPeriod: 'Extend trial period',
    extendedTo: 'Extend trial period to',
    periodFailed: 'trial period is failed',
    accountingNote: 'Finance team uses end of trial period resolutions in their reports and calculations.',
    setAsPassed: 'Set trial as passed',
    setAsFailed: 'Set trial as failed',
    confirmButtonText: 'Confirm trial result',
    periodEndedOn: 'Trial period ended on',
    periodExtendedTo: 'Trial period extended to',
    periodEndsOn: 'Trial period ends on',
    extendedPeriodTitle: 'Extended trial period',
    periodWillBeExtendedTo: 'Trial period will be extended to',
    closePeriod: 'Close trial period',
    periodPassedSuccessfully: 'End of trial period is passed successfully!',
    waitSetResolution: 'Wait... set end of trial period resolution',
    periodEstablished: 'Trial period established. Status:',
    periodCouldNotBeEstablished: 'end of the trial period could not be established',
    passingPeriod: 'passing trial period',
    periodPassingTitle: 'Passing trial period',
    accountingPassedNote:
      'Finance report will list {name} with passed trial status; however, the feedback still has to be gathered, and the evaluation has to be closed for it to be completed and the evaluatee notified via email.',
    periodFailedTitle: 'Trial period failed',
    failingReasonPlaceholder: 'Leave a note why the trial period is failed',
    extendReasonPlaceholder: 'Leave a note why the trial period is being extended',
    failingReasonLabel: 'Leave a note why the trial period is failed',
    extendReasonLabel: 'Leave a note why the trial period is being extended',
    extensionNewRecordNote:
      'Selecting "Extend trial period" will generate a new evaluation record, where a preliminary resolution and feedback will be required again, at the new date.',
  };
};
